import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Col,
  Dropdown,
  FormGroup,
  FormLabel,
  Modal,
  Row,
} from "react-bootstrap";
import moment from "moment";
import { registerLocale } from "react-datepicker";
import es from "date-fns/locale/es";

// Components
import { DatePicker } from "../../global/form/DatePicker";
import FormCheck from "../../global/form/FormCheck";
import ModalSuccess from "../../global/modals/ModalSuccess";

// Custom Styles
const customStyles = {
  dropdown: {
    width: "100%",
    height: "35px",
    border: "1px solid #bfc2c6",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
};

// Mock data for days of the week
const daysWeekMock = [
  {
    id: 1,
    name: "dayWeek.mondayShort",
    fullname: "dayWeek.monday",
    active: false,
  },
  {
    id: 2,
    name: "dayWeek.tuesdayShort",
    fullname: "dayWeek.tuesday",
    active: false,
  },
  {
    id: 3,
    name: "dayWeek.wednesdayShort",
    fullname: "dayWeek.wednesday",
    active: false,
  },
  {
    id: 4,
    name: "dayWeek.thursdayShort",
    fullname: "dayWeek.thursday",
    active: false,
  },
  {
    id: 5,
    name: "dayWeek.fridayShort",
    fullname: "dayWeek.friday",
    active: false,
  },
  {
    id: 6,
    name: "dayWeek.saturdayShort",
    fullname: "dayWeek.saturday",
    active: false,
  },
  {
    id: 7,
    name: "dayWeek.sundayShort",
    fullname: "dayWeek.sunday",
    active: false,
  },
];

// Options for repeat schedule
const repeatScheduleOptions = [
  { value: "weekly", label: "class:repeats.weekly" },
  { value: "biweekly", label: "class:repeats.biweekly" },
  { value: "monthly", label: "class:repeats.monthly" },
];

// Base schedule data
const currentTime = moment().startOf("hour");
const base = {
  index: "new",
  days: daysWeekMock.map((day) => ({ ...day })),
  start: currentTime,
  startView: currentTime.format("hh:mm A"),
  end: moment(currentTime).add(1, "hour"),
  endView: moment(currentTime).add(1, "hour").format("hh:mm A"),
  repeats: "weekly",
  repeatsView: "repeats.weekly",
  repeats_starts_on: moment(),
  repeatsStartsOnView: moment().format("DD/MM/YYYY"),
};

const ModalAddClassDays = ({ show, onHide, onSave, onDelete, schedule }) => {
  // Get language
  const prefix = process.env.REACT_APP_PREFIX;
  const language = localStorage.getItem(`cmLanguage${prefix}`);
  // Traslate calendar
  registerLocale("spanish", es);
  let localLanguage = {};
  if (language === "spanish") {
    localLanguage = { locale: "spanish" };
  }
  // Hooks
  const [t] = useTranslation(["global", "class"]);
  const [showModal, setShowModal] = useState(show);
  const [scheduleData, setScheduleData] = useState(base);
  const [errorDay, setErrorDay] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (schedule) setScheduleData(schedule);
  }, []);

  const saveSchedule = () => {
    const isSomeActive = scheduleData.days.some((day) => day.active);
    if (isSomeActive) {
      onSave(scheduleData);
      setErrorDay(false);
      onHide();
    } else {
      setErrorDay(true);
    }
  };

  return (
    <React.Fragment>
      <Modal show={showModal} size="lg" id="modalAddClassDays">
        <Modal.Header id="modalAddClassDaysHeader">
          <Modal.Title id="modalAddClassDaysTitle">
            {t("class:modalAddClassDays.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body id="modalAddClassDaysBody">
          <Row id="selectDaysTextContainer" className="mb-3">
            <Col xs={12} id="selectDaysText">
              {t("class:modalAddClassDays.selectDaysText")}
            </Col>
          </Row>
          <Row className="mb-3" id="daysTimeContainer">
            <Col xs={12} md={6} id="daysWeekContainer">
              <Row id="daysWeekLabelContainer">
                <Col>
                  <FormLabel>{t("class:modalAddClassDays.daysWeek")}</FormLabel>
                </Col>
              </Row>
              <Row id="daysWeekOptionsContainer">
                {scheduleData.days.map((day) => (
                  <Col key={day.id} xs={1} style={{ paddingRight: "35px" }}>
                    <FormCheck
                      id={"day" + day.id}
                      name={t("global:" + day.fullname)}
                      label={t("global:" + day.name)}
                      checked={day.active}
                      onChange={() => {
                        const daysCopy = scheduleData.days.map((d) => {
                          if (d.id === day.id) {
                            return { ...d, active: !d.active };
                          }
                          return d;
                        });
                        let scheduleDataCopy = { ...scheduleData };
                        scheduleDataCopy.days = daysCopy;
                        setScheduleData(scheduleDataCopy);
                      }}
                    />
                  </Col>
                ))}
              </Row>
            </Col>
            <Col xs={12} md={6} id="timesContainer">
              <Row id="timesRow">
                <Col xs={6} id="startTimeContainer">
                  <DatePicker
                    label={t("class:modalAddClassDays.start")}
                    placeholderText={"HH:MM AM/PM"}
                    name="startTime"
                    id="startTimePicker"
                    timeIntervals={1}
                    showTimeSelect
                    showTimeSelectOnly
                    minTime={new Date().setHours(0, 0, 0, 0)}
                    maxTime={new Date().setHours(23, 59, 59, 999)}
                    timeCaption={t("class:modalAddClassDays.time")}
                    dateFormat="hh:mm aa"
                    timeFormat="hh:mm aa"
                    {...localLanguage}
                    isClearable
                    value={scheduleData.startView}
                    onChange={(date) => {
                      let scheduleDataCopy = { ...scheduleData };
                      scheduleDataCopy.start = moment(date);
                      scheduleDataCopy.startView =
                        moment(date).format("hh:mm A");
                      setScheduleData(scheduleDataCopy);
                    }}
                  />
                </Col>
                <Col xs={6} id="endTimeContainer">
                  <DatePicker
                    label={t("class:modalAddClassDays.end")}
                    placeholderText={"HH:MM AM/PM"}
                    name="endTime"
                    id="endTimePicker"
                    timeIntervals={1}
                    showTimeSelect
                    showTimeSelectOnly
                    minTime={new Date().setHours(0, 0, 0, 0)}
                    maxTime={new Date().setHours(23, 59, 59, 999)}
                    timeCaption={t("class:modalAddClassDays.time")}
                    dateFormat="hh:mm aa"
                    timeFormat="hh:mm aa"
                    {...localLanguage}
                    isClearable
                    value={scheduleData.endView}
                    onChange={(date) => {
                      let scheduleDataCopy = { ...scheduleData };
                      scheduleDataCopy.end = moment(date);
                      scheduleDataCopy.endView = moment(date).format("hh:mm A");
                      setScheduleData(scheduleDataCopy);
                    }}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className="mb-3" id="repeatScheduleContainer">
            <Col xs={12} md={6}>
              <FormGroup>
                <FormLabel>{t("class:repeats.repeat")}</FormLabel>
                <Dropdown>
                  <Dropdown.Toggle
                    id="repeatScheduleDropdown"
                    variant="outline-secondary"
                    className="text-truncate"
                    style={customStyles.dropdown}
                  >
                    {t("class:" + scheduleData.repeatsView)}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {repeatScheduleOptions.map((item) => {
                      return (
                        <Dropdown.Item
                          key={item.value}
                          id={"repeatOption-" + item.value}
                          onClick={() => {
                            let scheduleDataCopy = { ...scheduleData };
                            scheduleDataCopy.repeats = item.value;
                            scheduleDataCopy.repeatsView =
                              "repeats." + item.value;
                            setScheduleData(scheduleDataCopy);
                          }}
                          className="text-truncate"
                        >
                          {t(item.label)}
                        </Dropdown.Item>
                      );
                    })}
                  </Dropdown.Menu>
                </Dropdown>
              </FormGroup>
            </Col>
            <Col xs={12} md={6}>
              <DatePicker
                label={t("class:repeats.as_of")}
                id="repeatsStartsOnDatePicker"
                dateFormat="dd/MM/yyyy"
                name="day"
                dropdownMode="select"
                required
                {...localLanguage}
                monthView={true}
                value={scheduleData.repeatsStartsOnView}
                onChange={(date) => {
                  let scheduleDataCopy = { ...scheduleData };
                  scheduleDataCopy.repeats_starts_on = moment(date);
                  scheduleDataCopy.repeatsStartsOnView =
                    moment(date).format("DD/MM/YYYY");
                  setScheduleData(scheduleDataCopy);
                }}
              />
            </Col>
          </Row>
          {errorDay && (
            <Row>
              <Col xs={12} className="mb-3">
                <Alert variant="warning" id="modalAddClassDaysError">
                  {t("class:modalAddClassDays.selectLeastOneDay")}
                </Alert>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer
          style={schedule ? { justifyContent: "space-between" } : {}}
        >
          {schedule && (
            <div>
              <Button
                id="deleteModalAddClassDaysBtn"
                variant="primary"
                onClick={() => {
                  setShowDeleteModal(true);
                  setShowModal(false);
                }}
                className="float-start"
              >
                {t("global:buttons.eliminate")}
              </Button>
            </div>
          )}
          <div>
            <Button
              id="cancelModalAddClassDaysBtn"
              variant="secondary"
              onClick={onHide}
            >
              {t("global:buttons.cancel")}
            </Button>
            <Button
              id="saveModalAddClassDaysBtn"
              variant="primary"
              onClick={saveSchedule}
            >
              {t("global:buttons.add")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      {showDeleteModal && (
        <ModalSuccess
          showModalSuccess={showDeleteModal}
          title={t("class:modalAddClassDays.deleteClassDay")}
          message={t("class:modalAddClassDays.deleteClassDayMessage")}
          fnAcceptButton={() => {
            setShowDeleteModal(false);
            onDelete(schedule.index);
            onHide();
          }}
          fnCancelButton={() => {
            setShowModal(true);
            setShowDeleteModal(false);
          }}
        />
      )}
    </React.Fragment>
  );
};

ModalAddClassDays.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  schedule: PropTypes.object,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
};

ModalAddClassDays.defaultProps = {
  show: false,
  onHide: () => {},
  schedule: null,
  onSave: () => {},
  onDelete: () => {},
};

export default ModalAddClassDays;
